import React, { useState } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const CalculatorModal = ({ open, onClose }) => {
  const [input, setInput] = useState("");
  const [result, setResult] = useState("0");

  // Helper function to perform basic arithmetic calculation
  const calculateResult = (expression) => {
    try {
      // Replace ÷ with /, × with *, and − with - for easier processing
      expression = expression
        .replace("÷", "/")
        .replace("×", "*")
        .replace("−", "-");

      // Parse the expression and calculate the result
      return calculateArithmetic(expression);
    } catch {
      return "Error";
    }
  };

  const calculateArithmetic = (expression) => {
    const operators = ["+", "-", "*", "/"];
    let stack = [];
    let currentNum = "";
    let lastOperator = null;

    for (let char of expression) {
      if (char === " " || operators.includes(char)) {
        if (currentNum) {
          stack.push(Number(currentNum));
          currentNum = "";
        }
        if (operators.includes(char)) {
          lastOperator = char;
        }
      } else {
        currentNum += char; // Collect digits for numbers
      }
    }

    if (currentNum) stack.push(Number(currentNum)); // Push the last number

    // Apply operators precedence
    // First apply *, /
    for (let i = 0; i < stack.length; i++) {
      if (stack[i] === "*" || stack[i] === "/") {
        const prevNum = stack[i - 1];
        const nextNum = stack[i + 1];
        const result = stack[i] === "*" ? prevNum * nextNum : prevNum / nextNum;
        stack.splice(i - 1, 3, result); // Replace the operation with the result
        i--; // Recheck the previous position
      }
    }

    // Then apply +, -
    let result = stack[0];
    for (let i = 1; i < stack.length; i += 2) {
      const operator = stack[i];
      const nextNum = stack[i + 1];
      result =
        operator === "+"
          ? result + nextNum
          : operator === "-"
          ? result - nextNum
          : result;
    }

    return result.toString();
  };

  const handleButtonClick = (value) => {
    if (value === "AC") {
      setInput("");
      setResult("0");
    } else if (value === "+/-") {
      setInput((prev) => (prev ? String(-parseFloat(prev)) : ""));
    } else if (value === "%") {
      setInput((prev) => (prev ? String(parseFloat(prev) / 100) : ""));
    } else if (value === "=") {
      const evaluatedResult = calculateResult(input);
      setResult(evaluatedResult);
      setInput("");
    } else {
      setInput((prevInput) => prevInput + value);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          position: "relative",
          backgroundColor: "black",
          borderRadius: "20px",
          padding: "20px",
          width: "340px",
          textAlign: "right",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 10, right: 10, color: "white" }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>

        {/* Display */}
        <Box
          sx={{
            color: "white",
            fontSize: "50px",
            fontWeight: 300,
            padding: "10px 0",
          }}
        >
          {input || result}
        </Box>

        {/* Buttons Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "10px",
            width: "100%",
            marginTop: "10px",
          }}
        >
          {[
            "AC",
            "+/-",
            "%",
            "÷",
            "7",
            "8",
            "9",
            "×",
            "4",
            "5",
            "6",
            "−",
            "1",
            "2",
            "3",
            "+",
            "0",
            ".",
            "=",
          ].map((btn) => (
            <Box
              key={btn}
              component="button"
              onClick={() => handleButtonClick(btn)}
              sx={{
                width: btn === "0" ? "160px" : "75px",
                height: "75px",
                border: "none",
                borderRadius: btn === "0" ? "40px" : "50%",
                fontSize: "28px",
                fontWeight: 500,
                backgroundColor: ["÷", "×", "−", "+", "="].includes(btn)
                  ? "#FF9500"
                  : ["AC", "+/-", "%"].includes(btn)
                  ? "#A5A5A5"
                  : "#333",
                color: ["AC", "+/-", "%"].includes(btn) ? "black" : "white",
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                "&:active": { filter: "brightness(1.2)" },
                gridColumn: btn === "0" ? "span 2" : "auto",
              }}
            >
              {btn}
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default CalculatorModal;
